import { useCallback, useState } from 'react';

interface SettleableQuery {
  error?: unknown;
  isFetching: boolean;
  isLoading: boolean;
  refetch: () => { requestId: string };
  requestId?: string;
}

/**
 * RTK Query refetches a rejected query whenever a new subscriber mounts, and keeps the previous error while that
 * refetch is pending, so its `isLoading`/`isFetching` flip back to true. If we surfaced that, callers gating on either
 * flag would swap to a skeleton, unmount subscribing children, and remount them when the call fails again, which is an
 * infinite fetch loop. Once a call has failed, report it as settled until it succeeds, except for the requests started
 * by `retry()`, which callers need to see as loading. Matching by `requestId` keeps that loop-safe: a refetch
 * triggered by a remount always gets a new id, so it stays settled.
 */
const useSettledQueryState = ({ error, isFetching, isLoading, refetch, requestId }: SettleableQuery) => {
  const [retryRequestIds, setRetryRequestIds] = useState<string[]>([]);
  const isSettledAfterError = !!error && !(requestId && retryRequestIds.includes(requestId));

  const retry = useCallback(() => {
    const { requestId: retryRequestId } = refetch();
    // Append rather than replace: a retry tapped while one is in flight gets an id that never runs, and replacing
    // would hide the loading state of the request that is actually running.
    setRetryRequestIds((previous) => [...previous, retryRequestId]);
  }, [refetch]);

  return {
    isFetching: isFetching && !isSettledAfterError,
    isLoading: isLoading && !isSettledAfterError,
    retry,
  };
};

export { useSettledQueryState };
